import { X as CloseIcon } from "lucide-react";
import { Copy, GreenThick } from "../../assets/icon";
import { useState } from "react";

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    shareLink: string;
    title?: string;
    description?: string;
    referralCode?: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({
    isOpen,
    onClose,
    shareLink,
    referralCode,
    title = "Share the Love, Get Rewarded!",

}) => {
    const [copied, setCopied] = useState(false);
    const [copiedType, setCopiedType] = useState<"link" | "code">("link");
    const [showConditions, setShowConditions] = useState(false);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareLink);
        setCopied(true);
        setCopiedType("link");
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCopyReferralCode = () => {
        if (referralCode) {
            navigator.clipboard.writeText(referralCode);
            setCopied(true);
            setCopiedType("code");
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (!isOpen) return null;

    // open sharing links in new tab or mobile app
    const handleShare = (platform: string, link: string, text?: string) => {
        const encodedLink = encodeURIComponent(link);
        const encodedText = encodeURIComponent(text || "");

        switch (platform) {
            case "whatsapp":
                // Mobile apps and WhatsApp Web
                return `https://api.whatsapp.com/send?text=${encodedText}%20${encodedLink}`;
            case "facebook":
                // Opens web share (mobile and desktop)
                return `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`;
            case "x":

                return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedLink}`;
            case "instagram":
                // Instagram doesn't accept direct sharing, so we'll handle this separately
                return "";
            case "linkedin":
                return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedLink}`;
            default:
                return link;
        }
    };

    const socialIcons = [
        {
            name: "WhatsApp",
            svg: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M31.9992 16C31.9992 24.8367 24.8359 32 15.9992 32C7.16328 31.9992 0 24.8359 0 16C0 7.16328 7.16328 0 16 0C24.8367 0 32 7.16328 32 16H31.9992Z" fill="#333D4D" />
                    <path d="M7.99805 24.0005L9.12271 19.8918C8.42871 18.6892 8.06405 17.3258 8.06471 15.9278C8.06671 11.5572 11.6234 8.00049 15.9934 8.00049C18.114 8.00115 20.1047 8.82715 21.602 10.3258C23.0987 11.8245 23.9227 13.8165 23.922 15.9352C23.92 20.3065 20.3634 23.8632 15.9934 23.8632C14.6667 23.8625 13.3594 23.5298 12.2014 22.8978L7.99805 24.0005ZM12.396 21.4625C13.5134 22.1258 14.58 22.5232 15.9907 22.5238C19.6227 22.5238 22.5813 19.5678 22.5833 15.9338C22.5847 12.2925 19.64 9.34049 15.996 9.33915C12.3614 9.33915 9.40471 12.2952 9.40338 15.9285C9.40271 17.4118 9.83738 18.5225 10.5674 19.6845L9.90138 22.1165L12.396 21.4625ZM19.9874 17.8198C19.938 17.7372 19.806 17.6878 19.6074 17.5885C19.4094 17.4892 18.4354 17.0098 18.2534 16.9438C18.072 16.8778 17.94 16.8445 17.8074 17.0432C17.6754 17.2412 17.2954 17.6878 17.18 17.8198C17.0647 17.9518 16.9487 17.9685 16.7507 17.8692C16.5527 17.7698 15.914 17.5612 15.1574 16.8858C14.5687 16.3605 14.1707 15.7118 14.0554 15.5132C13.94 15.3152 14.0434 15.2078 14.142 15.1092C14.2314 15.0205 14.34 14.8778 14.4394 14.7618C14.54 14.6472 14.5727 14.5645 14.6394 14.4318C14.7054 14.2998 14.6727 14.1838 14.6227 14.0845C14.5727 13.9858 14.1767 13.0105 14.012 12.6138C13.8507 12.2278 13.6874 12.2798 13.566 12.2738L13.186 12.2672C13.054 12.2672 12.8394 12.3165 12.658 12.5152C12.4767 12.7138 11.9647 13.1925 11.9647 14.1678C11.9647 15.1432 12.6747 16.0852 12.7734 16.2172C12.8727 16.3492 14.17 18.3505 16.1574 19.2085C16.63 19.4125 16.9994 19.5345 17.2867 19.6258C17.7614 19.7765 18.1934 19.7552 18.5347 19.7045C18.9154 19.6478 19.7067 19.2252 19.872 18.7625C20.0374 18.2992 20.0374 17.9025 19.9874 17.8198Z" fill="white" />
                </svg>

            ),
        },
        {
            name: "Facebook",
            svg: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M31.9992 16C31.9992 24.8367 24.8359 32 15.9992 32C7.16328 31.9992 0 24.8359 0 16C0 7.16328 7.16328 0 16 0C24.8367 0 32 7.16328 32 16H31.9992Z" fill="#333D4D" />
                    <path d="M20.7407 14.7588L20.4741 16.8993C20.429 17.2569 20.1262 17.5258 19.7671 17.5258H16.3002V26.4753C15.9346 26.5083 15.5641 26.5252 15.1897 26.5252C14.3521 26.5252 13.5347 26.4415 12.7447 26.282V17.5258H10.0783C9.83351 17.5258 9.63379 17.3253 9.63379 17.0797V14.4012C9.63379 14.1556 9.83351 13.9551 10.0783 13.9551H12.7447V9.93815C12.7447 7.47308 14.7347 5.4751 17.1901 5.4751H20.301C20.5458 5.4751 20.7455 5.67562 20.7455 5.92124V8.59972C20.7455 8.84534 20.5458 9.04586 20.301 9.04586H18.0783C17.0966 9.04586 16.301 9.84474 16.301 10.8312V13.9559H20.0344C20.4629 13.9559 20.7938 14.3328 20.7415 14.7596L20.7407 14.7588Z" fill="white" />
                </svg>

            ),
        },
        {
            name: "X",
            svg: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M31.9992 16C31.9992 24.8367 24.8359 32 15.9992 32C7.16328 31.9992 0 24.8359 0 16C0 7.16328 7.16328 0 16 0C24.8367 0 32 7.16328 32 16H31.9992Z" fill="#333D4D" />
                    <path d="M18.3086 14.5294L24.7809 7.60693H22.3231L17.2431 13.0412L13.3527 7.60693H6.62988L13.4348 17.1129L6.62988 24.3921H9.0877L14.501 18.6019L18.646 24.3921H25.3688L18.3086 14.5302V14.5294ZM10.0935 9.38668H12.437L21.9043 22.6108H19.5608L10.0935 9.38668Z" fill="white" />
                </svg>

            ),
        },
        {
            name: "Instagram",
            svg: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M31.9992 16C31.9992 24.8367 24.8359 32 15.9992 32C7.16328 31.9992 0 24.8359 0 16C0 7.16328 7.16328 0 16 0C24.8367 0 32 7.16328 32 16H31.9992Z" fill="#333D4D" />
                    <path d="M20.3418 7.60742C22.5788 7.60761 24.3925 9.42218 24.3926 11.6592V20.3418C24.3923 22.5786 22.5787 24.3924 20.3418 24.3926H11.6592C9.42223 24.3925 7.60765 22.5787 7.60742 20.3418V11.6592C7.60747 9.42211 9.42212 7.60751 11.6592 7.60742H20.3418ZM11.9482 9.05469C10.3506 9.0548 9.05371 10.3515 9.05371 11.9492V20.0527C9.05396 21.6502 10.3507 22.9462 11.9482 22.9463H20.0518C21.6492 22.9462 22.946 21.6502 22.9463 20.0527V11.9492C22.9463 10.3516 21.6494 9.05481 20.0518 9.05469H11.9482ZM16.0078 11.6592C18.4036 11.6592 20.3485 13.6043 20.3486 16C20.3486 18.3958 18.4036 20.3408 16.0078 20.3408C13.6122 20.3406 11.668 18.3957 11.668 16C11.6681 13.6044 13.6123 11.6594 16.0078 11.6592ZM16.0078 13.1055C14.4136 13.1057 13.1144 14.4057 13.1143 16C13.1143 17.5944 14.4135 18.8943 16.0078 18.8945C17.6023 18.8945 18.9023 17.5945 18.9023 16C18.9022 14.4056 17.6023 13.1055 16.0078 13.1055ZM20.6396 10.5029C21.1179 10.503 21.5078 10.8928 21.5078 11.3711C21.5076 11.8492 21.1178 12.2392 20.6396 12.2393C20.1614 12.2393 19.7717 11.8493 19.7715 11.3711C19.7715 10.8927 20.1613 10.5029 20.6396 10.5029Z" fill="white" />
                </svg>

            ),
        },
        {
            name: "LinkedIn",
            svg: (
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M31.9992 16C31.9992 24.8367 24.8359 32 15.9992 32C7.16328 31.9992 0 24.8359 0 16C0 7.16328 7.16328 0 16 0C24.8367 0 32 7.16328 32 16H31.9992Z" fill="#333D4D" />
                    <path d="M11.8955 13.0967C11.9804 13.0968 12.0488 13.1661 12.0488 13.251V23.2461C12.0488 23.331 11.9804 23.4003 11.8955 23.4004H8.76855C8.6836 23.4004 8.61426 23.331 8.61426 23.2461V13.251C8.61426 13.166 8.6836 13.0967 8.76855 13.0967H11.8955ZM19.9473 12.7529C21.8429 12.753 23.7245 14.1265 23.7246 16.874V22.7129C23.7246 23.0905 23.4157 23.3993 23.0381 23.3994H20.9775C20.5999 23.3994 20.2901 23.0905 20.29 22.7129V17.9043C20.29 16.9564 19.5211 16.1875 18.5732 16.1875C17.6254 16.1875 16.8564 16.9564 16.8564 17.9043V22.7129C16.8564 23.0905 16.5466 23.3994 16.1689 23.3994H14.1084C13.7308 23.3993 13.4219 23.0905 13.4219 22.7129V13.7822C13.4221 13.4048 13.7309 13.0958 14.1084 13.0957H16.1689C16.5465 13.0957 16.8562 13.4047 16.8564 13.7822V14.2227C17.5434 13.3328 18.6805 12.7529 19.9473 12.7529ZM10.3311 7.60059C11.4691 7.60069 12.3916 8.52403 12.3916 9.66211C12.3915 10.8001 11.469 11.7225 10.3311 11.7227C9.19299 11.7227 8.27064 10.8001 8.27051 9.66211C8.27051 8.52396 9.19291 7.60059 10.3311 7.60059Z" fill="white" />
                </svg>

            ),
        },
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 z-50">
            <div className="bg-white  rounded-3xl p-7 w-[340px] md:w-[570px] relative">
                {/* Close */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <CloseIcon size={24} />
                </button>

                {/* Title */}
                <h2 className="text-[18px] font-bold text-[#121212] mb-4">{title}</h2>


                {/* Icons */}
                <div className="flex gap-4 mb-4 flex-wrap">
                    {socialIcons.map((icon) => (
                        <button
                            key={icon.name}
                            onClick={() => {
                                if (icon.name.toLowerCase() === "instagram") {
                                    handleCopyReferralCode();

                                    // Small delay so the copy toast feels natural
                                    setTimeout(() => {

                                        setTimeout(() => {
                                            window.open("https://www.instagram.com",);
                                        }, 1000);
                                    }, 500);
                                } else {
                                    const url = handleShare(icon.name.toLowerCase(), shareLink, title);
                                    if (url) {
                                        window.open(url, "_blank");
                                    }
                                }

                            }}
                            className="rounded-full transition-transform hover:scale-110 focus:outline-none"
                            title={icon.name.toLowerCase() === "instagram" ? "Copy referral code" : `Share on ${icon.name}`}
                        >
                            {icon.svg}
                        </button>

                    ))}
                </div>

                <p className="text-[12px] mb-4 text-[#626161]"> Or share with link:</p>

                {/* Copy link */}
                <div className="bg-gray-100 rounded-xl p-4 flex items-center justify-between">
                    <span className="text-gray-700 text-sm flex-1 truncate pr-4">
                        {shareLink}
                    </span>
                    <button
                        onClick={handleCopyLink}
                        className="flex-shrink-0 p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <Copy />
                    </button>
                </div>

                {copied && (
                    <p className="text-green-600 text-sm mt-2 text-center">
                        {copiedType === "link" ? "Link copied to clipboard!" : "Referral code copied to clipboard!"}
                    </p>
                )}

                <div className="mt-4 bg-gray-100 rounded-xl p-4">
                    {/* Header (clickable) */}
                    <button
                        onClick={() => setShowConditions(!showConditions)}
                        className="flex items-center justify-between w-full"
                    >
                        <span className="text-[#00A86B] font-semibold">
                          VouchLinks Advertiser Reward Conditions
                        </span>

                        <span className="text-[#00A86B] text-lg">
                            {showConditions ?

                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_5522_8612)">
                                        <path d="M5.88 17L12 10.8192L18.12 17L20 15.0972L12 7L4 15.0972L5.88 17Z" fill="#00A86B" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5522_8612">
                                            <rect width="24" height="24" fill="white" transform="matrix(0 -1 -1 0 24 24)" />
                                        </clipPath>
                                    </defs>
                                </svg>

                                : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_5522_8592)">
                                        <path d="M5.88 7L12 13.1808L18.12 7L20 8.90283L12 17L4 8.90283L5.88 7Z" fill="#00A86B" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_5522_8592">
                                            <rect width="24" height="24" fill="white" transform="matrix(0 1 -1 0 24 0)" />
                                        </clipPath>
                                    </defs>
                                </svg>}
                        </span>
                    </button>

                    {/* Content */}
                    {showConditions && (
                        <div className="mt-4 text-sm text-[#333D4D] space-y-3">
                            <p>
                              VouchLinks advertiser rewards are released only when you fulfil the following conditions.
                            </p>

                            <div>
                                
                                <ul className="space-y-2">
                                    <li className="flex items-start gap-2">
                                        <GreenThick />
                                        Complete all required account verifications
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <GreenThick />
                                        Fulfil the advertiser's reward eligibility conditions
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <GreenThick />
                                        Orders with refunds, returns, or cancellations may not qualify
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <GreenThick /> Rewards are released only after merchant/ network approval
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <GreenThick /> Fraudulent, duplicate, or invalid referrals are not eligible
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <GreenThick /> Payouts are processed only after rewards are
                                        successfully validated
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};